import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useNotification from '../../hooks/useNotification';
import { ImageEditor } from '../common';

/**
 * ImageEditorDialog Component
 * 
 * A dialog that contains the ImageEditor component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {string} props.projectId - Optional project ID for saving images
 * @param {string} props.initialImageUrl - Initial image URL to edit
 */
const ImageEditorDialog = ({ 
  open, 
  onClose, 
  projectId, 
  initialImageUrl 
}) => {
  const { showNotification } = useNotification();

  const handleImageEdited = (images) => {
    if (images && images.length > 0) {
      showNotification('success', 'Image edited successfully!');
    }
  };

  const handleImageSaved = (savedImage) => {
    showNotification('success', 'Edited image saved to project!');
    
    // Close dialog after saving
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Edit Images
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Edit existing images by describing the changes you want to make. You can optionally provide a mask image to specify which areas to edit.
          </Typography>
        </Box>
        
        <ImageEditor
          projectId={projectId}
          initialImageUrl={initialImageUrl}
          onImageEdited={handleImageEdited}
          onImageSaved={handleImageSaved}
          showSaveOption={!!projectId}
          showFullControls={true}
          defaultOptions={{
            model: 'dall-e-2',
            size: '1024x1024'
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditorDialog;